import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { User, CreditCard, FileText, CheckCircle, Loader2, Download, Mail, Building2, Gift, Receipt } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { ReferralTab } from './Dashboard/ReferralTab';
import { InvoiceTab } from './Dashboard/InvoiceTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function DashboardPage() {
  const { token, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('perfil');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadType, setUploadType] = useState('');
  const [isDownloadingCert, setIsDownloadingCert] = useState(false);
  const [inboxMessages, setInboxMessages] = useState([]);
  const [inboxUnread, setInboxUnread] = useState(0);
  const [referralData, setReferralData] = useState(null);
  const [referralCode, setReferralCode] = useState('');
  const [referralDiscount, setReferralDiscount] = useState(null);
  const [isApplyingCode, setIsApplyingCode] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    fetchInboxMessages();
    const sessionId = searchParams.get('session_id');
    if (sessionId) pollPaymentStatus(sessionId, 0);
  }, []);

  useEffect(() => {
    if (activeTab === 'referidos' || activeTab === 'membresia') fetchReferralStats();
  }, [activeTab]);

  async function fetchReferralStats() {
    try {
      const response = await fetch(`${API}/referral/my-stats`, { headers: { 'Authorization': `Bearer ${token}` }});
      if (response.ok) setReferralData(await response.json());
    } catch (error) { console.error('Error:', error); }
  }

  async function applyReferralCode() {
    if (!referralCode.trim()) return;
    setIsApplyingCode(true);
    try {
      const response = await fetch(`${API}/referral/apply`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: referralCode.trim().toUpperCase() })
      });
      const data = await response.json();
      if (response.ok) setReferralDiscount(data);
      else { alert(data.detail || 'Código inválido'); setReferralDiscount(null); }
    } catch (error) { console.error('Error:', error); }
    setIsApplyingCode(false);
  }

  async function fetchDashboardData() {
    try {
      const response = await fetch(`${API}/dashboard/profile`, { headers: { 'Authorization': `Bearer ${token}` }});
      if (response.ok) setDashboardData(await response.json());
    } catch (error) { console.error('Error:', error); }
    setIsLoading(false);
  }

  async function fetchInboxMessages() {
    try {
      const response = await fetch(`${API}/inbox`, { headers: { 'Authorization': `Bearer ${token}` }});
      if (response.ok) {
        const data = await response.json();
        setInboxMessages(data.messages || []);
        setInboxUnread(data.unread_count || 0);
      }
    } catch (error) { console.error('Error:', error); }
  }

  async function markMessageRead(messageId) {
    await fetch(`${API}/inbox/${messageId}/read`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` }});
    setInboxMessages(prev => prev.map(m => m.id === messageId ? {...m, leido: true} : m));
    setInboxUnread(prev => Math.max(0, prev - 1));
  }

  async function handlePayment() {
    const response = await fetch(`${API}/membership/checkout`, {
      method: 'POST', 
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ origin_url: window.location.origin, referral_code: referralDiscount?.referral_code || '' })
    });
    if (response.ok) {
      const data = await response.json();
      window.location.href = data.url;
    }
  }

  async function pollPaymentStatus(sessionId, attempts) {
    if (attempts >= 10) return;
    try {
      const response = await fetch(`${API}/membership/checkout/status/${sessionId}`, { headers: { 'Authorization': `Bearer ${token}` }});
      if (response.ok) {
        const data = await response.json();
        if (data.payment_status === 'paid') { fetchDashboardData(); return; }
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) { console.error('Error:', error); }
  }

  async function handleFileUpload(e) {
    const file = e.target.files?.[0];
    if (!file || !uploadType) return;
    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch(`${API}/documents/upload?tipo=${uploadType}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
      });
      if (response.ok) { fetchDashboardData(); setUploadType(''); }
    } catch (error) { console.error('Error:', error); }
    setIsUploading(false);
  }

  async function handleDownloadCertificate() {
    setIsDownloadingCert(true);
    try {
      const response = await fetch(`${API}/certificate/download`, { headers: { 'Authorization': `Bearer ${token}` }});
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = 'certificado_cni.pdf';
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) { console.error('Error:', error); }
    setIsDownloadingCert(false);
  }

  if (isLoading) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-amber-400" /></div>;

  const { professional, membership } = dashboardData || {};
  const membershipStatus = membership?.status || 'Pendiente de Pago';
  const isActive = membershipStatus === 'Vigente' || membershipStatus === 'Por Vencer';

  return (
    <div className="page-transition min-h-screen bg-[#0a0a0a]" data-testid="dashboard-page">
      <div className="container-cni py-8">
        <DashboardHeader professional={professional} inboxUnread={inboxUnread} setActiveTab={setActiveTab} />
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[#1a1a1a] border border-[#2a2a2a] p-1 flex-wrap">
            <TabsTrigger value="perfil" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black"><User className="w-4 h-4 mr-2" />Perfil</TabsTrigger>
            <TabsTrigger value="buzon" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black"><Mail className="w-4 h-4 mr-2" />Buzón {inboxUnread > 0 && `(${inboxUnread})`}</TabsTrigger>
            <TabsTrigger value="asociaciones" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black"><Building2 className="w-4 h-4 mr-2" />Asociaciones</TabsTrigger>
            <TabsTrigger value="membresia" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black"><CreditCard className="w-4 h-4 mr-2" />Membresía</TabsTrigger>
            <TabsTrigger value="facturacion" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black"><Receipt className="w-4 h-4 mr-2" />Facturación</TabsTrigger>
            <TabsTrigger value="referidos" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black"><Gift className="w-4 h-4 mr-2" />Referidos</TabsTrigger>
            <TabsTrigger value="documentos" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black"><FileText className="w-4 h-4 mr-2" />Documentos</TabsTrigger>
          </TabsList>

          <TabsContent value="buzon" className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <h2 className="font-manrope font-semibold text-lg text-white mb-6 flex items-center"><Mail className="w-5 h-5 mr-2 text-amber-400" />Buzón de Mensajes del CNI</h2>
            <InboxList messages={inboxMessages} onMarkRead={markMessageRead} />
          </TabsContent>

          <TabsContent value="asociaciones" className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <h2 className="font-manrope font-semibold text-lg text-white mb-6 flex items-center"><Building2 className="w-5 h-5 mr-2 text-amber-400" />Mis Asociaciones</h2>
            <AssociationList associations={professional?.asociaciones} />
          </TabsContent>

          <TabsContent value="perfil" className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <h2 className="font-manrope font-semibold text-lg text-white mb-6">Mi Perfil Profesional</h2>
            <ProfileContent professional={professional} user={user} isActive={isActive} />
          </TabsContent>

          <TabsContent value="membresia" className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <h2 className="font-manrope font-semibold text-lg text-white mb-6">Membresía CNI</h2>
            <MembershipContent 
              membership={membership} membershipStatus={membershipStatus} isActive={isActive}
              referralData={referralData} referralDiscount={referralDiscount}
              referralCode={referralCode} setReferralCode={setReferralCode}
              isApplyingCode={isApplyingCode} applyReferralCode={applyReferralCode}
              handlePayment={handlePayment} handleDownloadCertificate={handleDownloadCertificate}
              isDownloadingCert={isDownloadingCert}
            />
          </TabsContent>

          <TabsContent value="referidos" className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <h2 className="font-manrope font-semibold text-lg text-white mb-6 flex items-center"><Gift className="w-5 h-5 mr-2 text-amber-400" />Programa de Referidos</h2>
            <ReferralTab token={token} isActive={isActive} referralData={referralData} setReferralData={setReferralData} onGoToMembership={() => setActiveTab('membresia')} />
          </TabsContent>

          <TabsContent value="facturacion" className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <h2 className="font-manrope font-semibold text-lg text-white mb-6 flex items-center"><Receipt className="w-5 h-5 mr-2 text-amber-400" />Facturación CFDI</h2>
            <InvoiceTab token={token} membership={membership} isActive={isActive} />
          </TabsContent>

          <TabsContent value="documentos" className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <h2 className="font-manrope font-semibold text-lg text-white mb-6">Mis Documentos</h2>
            <DocumentsContent 
              documents={professional?.documentos} uploadType={uploadType} setUploadType={setUploadType}
              handleFileUpload={handleFileUpload} isUploading={isUploading}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function DashboardHeader({ professional, inboxUnread, setActiveTab }) {
  return (
    <div className="flex items-center justify-between mb-8">
      <div>
        <h1 className="font-manrope font-bold text-2xl text-white mb-1">Bienvenido, {professional?.nombre?.split(' ')[0] || 'Profesional'}</h1>
        <p className="text-gray-400">Gestiona tu perfil y membresía del CNI</p>
      </div>
      {inboxUnread > 0 && (
        <button onClick={() => setActiveTab('buzon')} className="relative p-2 rounded-lg bg-[#1a1a1a] border border-[#2a2a2a]">
          <Mail className="w-5 h-5 text-gray-400" />
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">{inboxUnread}</span>
        </button>
      )}
    </div>
  );
}

function InboxList({ messages, onMarkRead }) {
  if (messages.length === 0) {
    return <div className="text-center py-12 text-gray-400"><Mail className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>No tienes mensajes</p></div>;
  }
  const items = [];
  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    items.push(
      <div key={msg.id} onClick={() => !msg.leido && onMarkRead(msg.id)}
        className={`p-4 rounded-lg border cursor-pointer ${msg.leido ? 'bg-[#1a1a1a] border-[#2a2a2a]' : 'bg-[#222222] border-amber-500/30'}`}>
        <div className="flex justify-between mb-2">
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs">{msg.tipo}</Badge>
          <span className="text-xs text-gray-500">{new Date(msg.created_at).toLocaleDateString('es-MX')}</span>
        </div>
        <h3 className="font-semibold text-white mb-1">{msg.titulo}</h3>
        <p className="text-gray-400 text-sm">{msg.contenido}</p>
      </div>
    );
  }
  return <div className="space-y-4">{items}</div>;
}

function AssociationList({ associations }) {
  const asocs = associations || [];
  if (asocs.length === 0) {
    return (
      <div className="text-center py-12">
        <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-600" />
        <p className="text-gray-400 mb-4">No estás afiliado a ninguna asociación</p>
        <Link to="/asociaciones"><Button className="bg-amber-500 hover:bg-amber-400 text-black font-semibold">Ver Asociaciones</Button></Link>
      </div>
    );
  }
  const items = [];
  for (let i = 0; i < asocs.length; i++) {
    const asoc = asocs[i];
    const statusClass = asoc.estatus_membresia === 'activa' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400';
    items.push(
      <div key={i} className="p-4 bg-[#222222] rounded-lg border border-[#2a2a2a]">
        <div className="flex justify-between mb-3">
          <h3 className="font-semibold text-white">{asoc.nombre}</h3>
          <Badge className={statusClass}>{asoc.estatus_membresia || 'Pendiente'}</Badge>
        </div>
        <div className="text-sm text-gray-400">Ingreso: {asoc.fecha_ingreso ? new Date(asoc.fecha_ingreso).toLocaleDateString('es-MX') : 'N/A'}</div>
      </div>
    );
  }
  return <div className="space-y-4">{items}</div>;
}

function ProfileContent({ professional, user, isActive }) {
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <div><Label className="text-gray-400">Nombre</Label><p className="text-white font-medium">{professional?.nombre || 'No disponible'}</p></div>
        <div><Label className="text-gray-400">Email</Label><p className="text-white">{professional?.email || user?.email}</p></div>
        <div><Label className="text-gray-400">Teléfono</Label><p className="text-white">{professional?.telefono || 'No disponible'}</p></div>
        <div><Label className="text-gray-400">Ubicación</Label><p className="text-white">{professional?.estado || 'N/A'} / {professional?.ciudad || 'N/A'}</p></div>
      </div>
      <div className="space-y-4">
        <div><Label className="text-gray-400">Registro</Label><p className="text-white font-mono text-lg">{professional?.numero_registro || 'Pendiente'}</p></div>
        <div><Label className="text-gray-400">Rango</Label><Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">{professional?.rango || 'Profesional'}</Badge></div>
        <div><Label className="text-gray-400">Estatus</Label><Badge className={isActive ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>{professional?.estatus || 'Pendiente'}</Badge></div>
      </div>
    </div>
  );
}

function MembershipContent({ membership, membershipStatus, isActive, referralData, referralDiscount, referralCode, setReferralCode, isApplyingCode, applyReferralCode, handlePayment, handleDownloadCertificate, isDownloadingCert }) {
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <div><Label className="text-gray-400">Estado</Label><Badge className={isActive ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>{membershipStatus}</Badge></div>
        {membership?.fecha_vencimiento && <div><Label className="text-gray-400">Vencimiento</Label><p className="text-white">{new Date(membership.fecha_vencimiento).toLocaleDateString('es-MX')}</p></div>}
        {referralData?.available_credit > 0 && (
          <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
            <p className="text-green-400 text-sm"><Gift className="w-4 h-4 inline mr-1" />Tienes ${referralData.available_credit.toFixed(2)} MXN de crédito</p>
          </div>
        )}
      </div>
      <MembershipActions 
        isActive={isActive} referralDiscount={referralDiscount} referralCode={referralCode}
        setReferralCode={setReferralCode} isApplyingCode={isApplyingCode} applyReferralCode={applyReferralCode}
        handlePayment={handlePayment} handleDownloadCertificate={handleDownloadCertificate} isDownloadingCert={isDownloadingCert}
      />
    </div>
  );
}

function MembershipActions({ isActive, referralDiscount, referralCode, setReferralCode, isApplyingCode, applyReferralCode, handlePayment, handleDownloadCertificate, isDownloadingCert }) {
  if (isActive) {
    return (
      <div className="flex flex-col justify-center items-center p-6 bg-[#111111] rounded-lg">
        <CheckCircle className="w-12 h-12 text-green-400 mb-4" />
        <p className="text-white font-semibold mb-4">Membresía Activa</p>
        <Button onClick={handleDownloadCertificate} disabled={isDownloadingCert} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold">
          {isDownloadingCert ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}Descargar Certificado
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col justify-center items-center p-6 bg-[#111111] rounded-lg">
      <CreditCard className="w-12 h-12 text-amber-400 mb-4" />
      <p className="text-white font-semibold mb-2">Activa tu Membresía</p>
      <p className="text-gray-400 text-sm mb-4">
        {referralDiscount ? (
          <span><span className="line-through">${referralDiscount.original_price.toFixed(2)}</span> <span className="text-green-400 font-bold">${referralDiscount.final_price.toFixed(2)} MXN</span></span>
        ) : '$1,740 MXN / año'}
      </p>
      <div className="w-full mb-4">
        <Label className="text-gray-400 text-xs">¿Tienes código de referido?</Label>
        <div className="flex gap-2 mt-1">
          <input type="text" value={referralCode} onChange={(e) => setReferralCode(e.target.value.toUpperCase())} placeholder="CNI-XXXXXX" className="flex-1 p-2 bg-[#222222] border border-[#3a3a3a] rounded-lg text-white text-sm" />
          <Button onClick={applyReferralCode} disabled={isApplyingCode || !referralCode.trim()} variant="outline" className="border-amber-500 text-amber-400 hover:bg-amber-500/20">
            {isApplyingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aplicar'}
          </Button>
        </div>
        {referralDiscount && <p className="text-green-400 text-xs mt-1">Código de {referralDiscount.referrer_name} aplicado</p>}
      </div>
      <Button onClick={handlePayment} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold w-full">Pagar Membresía</Button>
    </div>
  );
}

function DocumentsContent({ documents, uploadType, setUploadType, handleFileUpload, isUploading }) {
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <h3 className="text-white font-medium mb-4">Subir Documento</h3>
        <select value={uploadType} onChange={(e) => setUploadType(e.target.value)} className="w-full p-2 mb-4 bg-[#111111] border border-[#2a2a2a] rounded-lg text-white">
          <option value="">Seleccionar tipo...</option>
          <option value="identificacion">Identificación</option>
          <option value="comprobante_domicilio">Comprobante Domicilio</option>
          <option value="cedula_profesional">Cédula Profesional</option>
          <option value="certificacion">Certificación</option>
        </select>
        {uploadType && <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileUpload} disabled={isUploading} className="text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-amber-500 file:text-black file:font-semibold" />}
      </div>
      <div><h3 className="text-white font-medium mb-4">Documentos Subidos</h3><DocumentList documents={documents} /></div>
    </div>
  );
}

function DocumentList({ documents }) {
  const docs = documents || [];
  if (docs.length === 0) return <p className="text-gray-400 text-sm">No has subido documentos</p>;
  const items = [];
  for (let i = 0; i < docs.length; i++) {
    const doc = docs[i];
    const statusClass = doc.status === 'Aprobado' ? 'bg-green-500/20 text-green-400' : doc.status === 'Rechazado' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400';
    items.push(
      <div key={i} className="flex justify-between p-3 bg-[#111111] rounded-lg">
        <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-amber-400" /><span className="text-gray-300 text-sm">{doc.nombre_archivo || doc.tipo}</span></div>
        <Badge className={statusClass}>{doc.status || 'Pendiente'}</Badge>
      </div>
    );
  }
  return <div className="space-y-2">{items}</div>;
}

export default DashboardPage;
